import * as THREE from 'three';
import type { DamageFact } from '../../structures/demolition/demolitionDamageLedger';
import { hash01 } from '../fallback/impactFxMath';
import type { DamageFieldClock } from './damageFieldTexture';
import type { PackedDecalStamps } from '../../materials/contracts/decalStamps';

export type { PackedDecalStamps } from '../../materials/contracts/decalStamps';

export const DECAL_STAMP_CAPACITY = 48;
/** Live ring occupancy that triggers a bake-down of the oldest batch. */
export const BAKE_DOWN_TRIGGER = 40;
/** Stamps retired into the baked maps per bake-down event. */
export const BAKE_DOWN_BATCH = 24;
/** Applied-energy scale that maps min-power scuffs under the light-row threshold. */
export const STAMP_ENERGY_CALIBRATION_J = 3200;
/** Below this → light-scuff row (shader also gates medium/heavy). */
export const DECAL_ENERGY_ROW_THRESHOLD = 0.28;
export const DECAL_ENERGY_MEDIUM_THRESHOLD = 0.58;
export const DECAL_TILE_SIZE_LIGHT = 0.35;
export const DECAL_TILE_SIZE_HEAVY = 1.0;
export const DECAL_VARIANT_COUNT = 4;
/** Grazing above this selects the gouge atlas row (live contacts rarely exceed ~0.4). */
export const DECAL_GRAZING_GOUGE_THRESHOLD = 0.28;

/** Wood's carve band is tens–hundreds of J; 3200 J is the concrete KE scale. */
export const STAMP_ENERGY_CALIBRATION_WOOD_J = 22;

export function stampVisualEnergy(
  fact: DamageFact,
  calibrationJ: number = STAMP_ENERGY_CALIBRATION_J,
): number {
  const cal = calibrationJ > 1 ? calibrationJ : STAMP_ENERGY_CALIBRATION_J;
  // Wood's carve band is tens of J. A linear /22 map parked light+medium in
  // the same <0.28 light row (3.4 J vs 3.8 J). Piecewise: 0–5 J light,
  // 5–18 J medium, 18+ heavy. Grazing still overrides via stampAtlasRow.
  if (cal === STAMP_ENERGY_CALIBRATION_WOOD_J || (cal > 1 && cal < 100)) {
    const j = Number.isFinite(fact.appliedEnergy) ? fact.appliedEnergy : 0;
    let value: number;
    // Wood carves around ~5–6 J, so the medium band must start below that.
    if (j <= 3.5) value = (j / 3.5) * 0.27;
    else if (j <= 16) value = 0.28 + ((j - 3.5) / 12.5) * 0.29;
    else value = 0.58 + Math.min(1, (j - 16) / 80) * 0.42;
    return Math.min(1, Math.max(0, value));
  }
  const fromApplied = fact.appliedEnergy / cal;
  const fromSeverity = Math.max(fact.normalizedSeverity, fact.damageAfter * 0.58) * 0.85;
  // A topology change is not a heavy stamp by itself. Weak walls (wood)
  // carve at scuff energy; forcing 0.85 hid every severity behind one puncture.
  const value = fromApplied >= DECAL_ENERGY_MEDIUM_THRESHOLD
    ? Math.max(fromApplied, fromSeverity)
    : fromApplied;
  return Number.isFinite(value) ? Math.min(1, Math.max(0, value)) : 0;
}

/** Energy-scaled influence radius for brick spall / stair reach. */
export function stampInfluenceRadius(
  fact: DamageFact,
  calibrationJ: number = STAMP_ENERGY_CALIBRATION_J,
): number {
  const energy = stampVisualEnergy(fact, calibrationJ);
  const kernel = Number.isFinite(fact.radius) && fact.radius > 0 ? fact.radius : 1.15;
  return Math.max(0.14, kernel * (0.42 + energy * 0.58));
}

export function stampTangentAngle(fact: DamageFact): number {
  const tx = fact.impactTangent?.x ?? 1;
  const ty = fact.impactTangent?.y ?? 0;
  const len = Math.hypot(tx, ty);
  if (len < 1e-4) return 0;
  return Math.atan2(ty / len, tx / len);
}

export function stampGrazing(fact: DamageFact): number {
  const g = fact.impactGrazing;
  return Number.isFinite(g) ? Math.min(1, Math.max(0, g as number)) : 0;
}

export function stampVariantIndex(sequence: number): number {
  const seed2 = hash01(sequence * 17 + 3);
  return Math.floor(seed2 * DECAL_VARIANT_COUNT) % DECAL_VARIANT_COUNT;
}

/** Atlas row 0 heavy / 1 medium / 2 light / 3 gouge — mirrors shader. */
export function stampAtlasRow(energy: number, grazing: number): number {
  if (grazing > DECAL_GRAZING_GOUGE_THRESHOLD) return 3;
  if (energy < DECAL_ENERGY_ROW_THRESHOLD) return 2;
  if (energy < DECAL_ENERGY_MEDIUM_THRESHOLD) return 1;
  return 0;
}

export function createDecalStampBuffers(): PackedDecalStamps {
  return {
    count: 0,
    a: Array.from({ length: DECAL_STAMP_CAPACITY }, () => new THREE.Vector4()),
    b: Array.from({ length: DECAL_STAMP_CAPACITY }, () => new THREE.Vector4()),
    r: Array.from({ length: DECAL_STAMP_CAPACITY }, () => 0),
    sequences: Array.from({ length: DECAL_STAMP_CAPACITY }, () => -1),
  };
}

export function clearDecalStampBuffers(packed: PackedDecalStamps): void {
  packed.count = 0;
  for (let i = 0; i < DECAL_STAMP_CAPACITY; i += 1) {
    packed.a[i].set(0, 0, 0, 0);
    packed.b[i].set(0, 0, 0, 0);
    packed.r[i] = 0;
    packed.sequences[i] = -1;
  }
}

/** Copy `count` stamps starting at `start` into a new packed buffer (GPU-sized). */
export function slicePackedDecalStamps(
  packed: PackedDecalStamps,
  start: number,
  count: number,
): PackedDecalStamps {
  const out = createDecalStampBuffers();
  const n = Math.max(0, Math.min(count, packed.count - start));
  out.count = n;
  for (let i = 0; i < n; i += 1) {
    const src = start + i;
    out.a[i].copy(packed.a[src]);
    out.b[i].copy(packed.b[src]);
    out.r[i] = packed.r[src];
    out.sequences[i] = packed.sequences[src];
  }
  return out;
}

/**
 * Pack the most recent 48 facts. `birthNormalized` is computed from the live
 * clock so epoch rebases stay correct without rewriting stored simTime.
 *
 * b.w = variant (0..3 integer) + grazing * 0.99 (fractional).
 * Scale/opacity jitter, mirror flip, dual-tile composite, and row selection
 * are derived in the shader from energy + seed (keeps the ring compact).
 */
export function packDecalStamps(
  facts: readonly DamageFact[],
  clock: Pick<DamageFieldClock, 'normalize'>,
  restPositions: ReadonlyMap<number, THREE.Vector3> | ((fact: DamageFact) => THREE.Vector3),
  packed: PackedDecalStamps,
  calibrationJ: number = STAMP_ENERGY_CALIBRATION_J,
  skipSequences?: ReadonlySet<number>,
): PackedDecalStamps {
  const liveFacts = skipSequences && skipSequences.size > 0
    ? facts.filter((fact) => !skipSequences.has(fact.sequence))
    : facts;
  const recent = liveFacts.length > DECAL_STAMP_CAPACITY
    ? liveFacts.slice(-DECAL_STAMP_CAPACITY)
    : liveFacts;
  packed.count = recent.length;
  for (let i = 0; i < DECAL_STAMP_CAPACITY; i += 1) {
    if (i >= recent.length) {
      packed.a[i].set(0, 0, 0, 0);
      packed.b[i].set(0, 0, 0, 0);
      packed.r[i] = 0;
      packed.sequences[i] = -1;
      continue;
    }
    const fact = recent[i];
    const rest = typeof restPositions === 'function'
      ? restPositions(fact)
      : restPositions.get(fact.sequence);
    const px = rest?.x ?? fact.position.x;
    const py = rest?.y ?? fact.position.y;
    const pz = rest?.z ?? fact.position.z;
    const energy = stampVisualEnergy(fact, calibrationJ);
    const seed = hash01(fact.sequence);
    const variant = stampVariantIndex(fact.sequence);
    const grazing = stampGrazing(fact);
    packed.a[i].set(px, py, pz, stampTangentAngle(fact));
    packed.b[i].set(
      energy,
      seed,
      clock.normalize(fact.simTime),
      variant + grazing * 0.99,
    );
    packed.r[i] = stampInfluenceRadius(fact, calibrationJ);
    packed.sequences[i] = fact.sequence;
  }
  return packed;
}
